/***************************************************************************/
"use client";
/***************************************************************************/

import {
  Alert,
  AlertContent,
  AlertIcon,
  AlertTitle,
} from "@/client/components/ui/alert";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormActions,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormProvider,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFormData, TLoginActionResult } from "../types";

const LoginForm: React.FC<{
  redirectUrl?: string;
  onLogin: (formData: TFormData) => Promise<TLoginActionResult | undefined>;
}> = ({ redirectUrl, onLogin }) => {
  const { t: tL } = useTranslation("l");
  const { t: tN } = useTranslation("n");
  const { t: tAuth } = useTranslation("auth");

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<
    TLoginActionResult | undefined
  >(undefined);
  const { formErrors, fieldErrors } = submitResult ?? {};

  const formSchema = z.object({
    email: z.string().min(1, {
      message: tAuth("login.form.validation.email.required"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  async function handleEmailValidation(email: string) {
    setSubmitting(true);

    const result = await onLogin({ email, redirectUrl });
    setSubmitResult(result);

    const hasError =
      (result?.fieldErrors?.email ?? []).length > 0 ||
      (result?.formErrors ?? []).length > 0;

    setSubmitting(!hasError);
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    handleEmailValidation(values.email);
  }

  return (
    <Card size="lg" className="w-[340px]">
      <CardHeader>
        <CardTitle>{tL("login")}</CardTitle>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <Form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tAuth("login.form.title")}</FormLabel>
                  <FormControl>
                    <Input
                      size="lg"
                      placeholder="hello@airfi.aero"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  {(fieldErrors?.email ?? []).length > 0 && (
                    <FormMessage className="flex flex-col">
                      {fieldErrors?.email?.map((message) => {
                        return <span key={message}>{message}</span>;
                      })}
                    </FormMessage>
                  )}
                </FormItem>
              )}
            />
            <FormActions className="flex justify-between items-center">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={submitting}
                loading={submitting}
              >
                <span>{tN("next")}</span>
              </Button>
            </FormActions>
            {!submitting && (formErrors ?? []).length > 0 && (
              <Alert variant="error">
                <AlertIcon />
                <AlertContent>
                  <AlertTitle>
                    {formErrors?.map((message) => (
                      <div key={message}>{message}</div>
                    ))}
                  </AlertTitle>
                </AlertContent>
              </Alert>
            )}
          </Form>
        </FormProvider>
      </CardContent>
    </Card>
  );
};

export { LoginForm };
